import { createContext, useContext } from 'react';

/**
 * "Is this table's felt actually painted on screen right now?"
 *
 * Distinct from the two gates it is often confused with:
 *
 *   - `TableActiveContext` — "is this the table the player is ON". In the
 *     single-table carousel that doubles as visibility, but in the multi-table
 *     grid it is the FOCUSED-cell gate (the ambient-sound scope, EN-10577) and
 *     in a tiled 2-up only one of the two VISIBLE tiles is active.
 *   - `GridModeContext` — "is this felt rendered smaller than the viewport".
 *     A sizing/layout fact. True for MultiTableGrid's cells AND for the
 *     carousel's tiled panels, including the ones that are parked off screen.
 *
 * Neither answers "can the player see this". That matters for anything that
 * starts a clock or escalates on a table's behalf:
 *
 *   - The multi-table grid stays MOUNTED behind single-table view
 *     (MultiTableGrid.tsx), so every cell is `gridMode` with no player looking.
 *   - RebindingTableView keeps recently-visited panels mounted and parked
 *     (`visibility: hidden`), and in tiled mode those are `gridMode` too.
 *
 * EN-10324: gating the table-load timeout on `gridMode || isActiveTable` let
 * both of those run a 20s failure clock for a table nobody was looking at, and
 * because the failure latches (see hooks/useTableLoadTimeout), the player met a
 * stale "error loading the table" the instant they switched to that surface.
 * The same signal gates `useFeltHydrationRecovery`, so the recovery reconnect
 * and the error that follows it always cover the same set of tables.
 *
 * SCOPE: this is SURFACE visibility, not viewport intersection. The grid's row
 * scrolls horizontally and holds every open table (`visibleSlots = slots`;
 * `tablesAcross` only sets each cell's width), so a cell scrolled past the edge
 * of an ON-SCREEN grid still reads `true`. That is deliberate, not an oversight:
 *
 *   - It cannot produce a stale error. `useTableLoadTimeout` clears its latch
 *     the moment the felt hydrates, so a cell that timed out while scrolled off
 *     and then hydrated is clean again before the player reaches it. What they
 *     do find on arrival is a failure that is still true right then — and the
 *     alternative is another full 20s of spinner after they scroll to it.
 *   - It cannot steal the recovery reconnect. `useFeltHydrationRecovery` forces
 *     a GLOBAL reconnect that re-subscribes every open table, behind a
 *     per-gameID one-shot and a module-level cooldown whose stated purpose is
 *     to collapse several stuck cells onto one attempt. An off-row cell firing
 *     it re-hydrates the on-screen cells too.
 *
 * Narrowing this to true intersection would mean per-cell React state driven by
 * `gridScrollChannel`, which exists specifically so scroll frames never re-render
 * the felt. Not worth that for a case with no demonstrated harm.
 *
 * A consumer that does need intersection reads `gridScrollChannel` itself, as
 * GameModal's dialog focus does (EN-11109): an off-row cell's dialog taking
 * focus would send the player's Escape or Space to a card they cannot see.
 *
 * Defaults to `true` so every surface without a provider is unchanged: the
 * lobby, storybook, the demo table and tests.
 */
export const TableOnScreenContext = createContext<boolean>(true);

export const TableOnScreenProvider = TableOnScreenContext.Provider;

/** True when this table's felt is on screen for the player right now. */
export const useIsTableOnScreen = (): boolean => useContext(TableOnScreenContext);

export default TableOnScreenContext;
